import React from 'react';
import PropTypes, { string, func } from 'prop-types';
import NikeAIShimmerButton from '../NikeAIShimmerButton';
import { StyledContainer, StyledQueryDisplay } from './styles';

/**
 * AskNikeAI Search Result Component
 * Displays a clickable "Ask NikeAI" option in search results
 * Shows user's search query as they type (or placeholder text)
 * Opens the Nike AI chat interface when clicked
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.searchQuery - The current search query to display
 * @param {string} props.placeholder - Placeholder text when no query (default: "Ask NikeAI")
 * @param {Function} props.onClick - Handler for when the item is clicked
 * @param {string} props.ariaLabel - Accessible label for screen readers
 * @returns {React.Element}
 */
const AskNikeAISearchResult = ({
  searchQuery = '',
  placeholder = 'Ask NikeAI',
  onClick,
  ariaLabel = 'Open Nike AI to ask questions about products',
  tabIndex = 0,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (window.webShellClient?.__WS_INTERNAL__?.nikeAI?.toggle) {
      // Fallback to controller if no onClick provided
      window.webShellClient.__WS_INTERNAL__.nikeAI.toggle();
    }
  };

  const displayText = searchQuery || placeholder;

  return (
    <StyledContainer
      aria-label={ariaLabel}
      data-testid="ask-nikeai-search-result"
      onClick={handleClick}
      role="button"
      tabIndex={tabIndex}
    >
      <NikeAIShimmerButton
        ariaLabel="Nike AI"
        onClick={e => {
          e.stopPropagation();
          handleClick();
        }}
        size="small"
        tabIndex={-1}
      />
      <StyledQueryDisplay
        aria-label={`Search query: ${displayText}`}
        readOnly
        tabIndex={-1}
        type="text"
        value={displayText}
      />
    </StyledContainer>
  );
};

AskNikeAISearchResult.propTypes = {
  ariaLabel: string,
  onClick: func,
  placeholder: string,
  searchQuery: string,
  tabIndex: PropTypes.number,
};

AskNikeAISearchResult.defaultProps = {
  ariaLabel: 'Open Nike AI to ask questions about products',
  onClick: undefined,
  placeholder: 'Ask NikeAI',
  searchQuery: '',
  tabIndex: 0,
};

export default AskNikeAISearchResult;
